import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";

import FormDescriptionField from "components/Environment/components/FormItems/DescriptionField";
import FormFileUploadField from "components/Environment/components/FormItems/FileUploadField";
import FormIsSecretField from "components/Environment/components/FormItems/IsSecretField";
import FormPathField from "components/Environment/components/FormItems/PathField";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Drawer from "ds/components/Drawer";
import Card from "ds/components/Card";

import CreateFileButton from "../ContextSetupEnvironmentForm/CreateFileButton";
import { ContextAttachedMountedFile } from "../types";
import ContentDrawer from "./ContentDrawer";

type ContextMountedFilesAddFormProps = {
  onSave: (item: ContextAttachedMountedFile) => void;
};

const ContextMountedFilesAddForm = ({ onSave }: ContextMountedFilesAddFormProps) => {
  const builderForm = useForm<ContextAttachedMountedFile>({
    defaultValues: {
      path: "",
      isSecret: true,
      description: "",
      file: null,
      content: null,
    },
    mode: "onChange",
  });

  const {
    handleSubmit,
    reset,
    watch,
    getValues,
    clearErrors,
    setValue,
    formState: { isValid },
  } = builderForm;
  const [isContentDrawerVisible, setIsContentDrawerVisible] = useState(false);

  const file = watch("file");
  const content = watch("content");
  const path = watch("path");

  const handleCloseDrawer = useCallback(() => setIsContentDrawerVisible(false), []);
  const handleOpenDrawer = useCallback(() => setIsContentDrawerVisible(true), []);

  const onSubmit = useCallback(
    () =>
      handleSubmit((formData: ContextAttachedMountedFile) => {
        onSave(formData);
        reset();
        handleCloseDrawer();
      })(),
    [onSave, reset, handleCloseDrawer, handleSubmit]
  );

  useEffect(() => {
    // After user selects a file check if path field is empty, if so, set it to file name
    if (!getValues("path") && file) {
      clearErrors("path");
      setValue("path", file.name);
    }
  }, [getValues, setValue, clearErrors, file]);

  return (
    <Card variant="secondary">
      <FormProvider {...builderForm}>
        <Box margin="0 0 large">
          <FormPathField name="path" />
        </Box>

        <FormIsSecretField
          name="isSecret"
          description="Secret values are only made available to Runs and Tasks and are not accessible in the web GUI and through the API. Marking files as a secret is a recommended option."
        />

        <FormDescriptionField name="description" />

        <Box margin="large 0 0" grid gridTemplate={file ? "1fr" : "1fr 1fr"} gap="large">
          <FormFileUploadField name="file" required={!content} />

          {!file && <CreateFileButton onClick={handleOpenDrawer} disabled={!path} />}
        </Box>
        {file && (
          <Box justify="end" fullWidth margin="large 0 0">
            <Button variant="contrast" onPress={onSubmit} disabled={!isValid}>
              Add file
            </Button>
          </Box>
        )}
        <Drawer position="absoluteRight" visible={isContentDrawerVisible} isResizable>
          <ContentDrawer
            onDrawerClose={handleCloseDrawer}
            name="content"
            onSave={handleSubmit(onSubmit)}
          />
        </Drawer>
      </FormProvider>
    </Card>
  );
};

export default ContextMountedFilesAddForm;
